import { Static, Type } from '@sinclair/typebox';
import { AiFieldKind } from './ai-assist';
import { COLOR_OPTIONS } from './colors/color_options';
import { CroppableImageSchema, THUMBOR_META_KEY } from './image';
import { ResizedImageLogoSchema, ResizedImageSchema } from './public-professional';
import { hexColorPattern } from './professional';
import { ProNewsDistributionStatus } from './namespace';
import { SortConfig, SortDirection, sortNullsLast } from './filter/sort';
import type { RichTextTag } from './website_types/BuilderTypes';
import { SeoArticleFaqItemSchema } from './seo/seo-article.schema';

const maxNewsTagSelectable = 1;

/**
 * Balises acceptées dans le corps d'une publication : une seule liste pour la saisie manuelle
 * (éditeur), la validation du schéma et les articles écrits par le moteur SEO (`ProNewsAdapter`).
 * Pas de `h1` : le titre de la publication est DÉJÀ rendu en `<h1>` par la page (`news-detail`),
 * un H1 dans le corps ferait un doublon (règle `SINGLE_H1` du score SEO). Les H1 historiques ont
 * été rétrogradés en H2 par la migration `pro_news_content_h1_to_h2`.
 */
export const PRO_NEWS_CONTENT_TAGS: RichTextTag[] = ['h2', 'h3', 'b', 'i', 'u', 'a', 'ul', 'ol', 'table'];

/**
 * Sentinelle stockée dans `pro_news_distribution.error_data.message` quand le pro n'a
 * aucune fiche Google publiable au moment de la distribution. Partagée pour que le
 * backend (qui l'écrit) et le frontend (qui l'interprète pour afficher « GMB non
 * connecté ») ne divergent jamais sur la chaîne exacte.
 */
export const GMB_NO_ELIGIBLE_LOCATION_ERROR = 'NO_ELIGIBLE_GOOGLE_LOCATION';

/**
 * Une publication produit UNE distribution par fiche Google : chaque fiche réussit ou
 * échoue indépendamment. Priorité d'affichage : un échec ne doit jamais être masqué par
 * une réussite, et une tentative en cours prime sur un envoi déjà abouti.
 */
const GMB_STATUS_PRIORITY: ProNewsDistributionStatus[] = [
  ProNewsDistributionStatus.ERROR,
  ProNewsDistributionStatus.SENDING,
  ProNewsDistributionStatus.PENDING,
  ProNewsDistributionStatus.SENT,
];

/** Forme minimale attendue d'une distribution, compatible avec le type généré par Orval. */
export type GmbDistributionLike = {
  status?: string | null;
  error_message?: string | null;
  is_stuck?: boolean | null;
};

export type GmbDistributionSummary = {
  /** Statut agrégé à afficher, `undefined` quand il n'y a aucune distribution. */
  status?: ProNewsDistributionStatus;
  /** Au moins une fiche en échec. */
  has_error: boolean;
  /** L'échec vient d'une absence de fiche publiable, pas d'un refus de Google. */
  is_no_account: boolean;
  /** Au moins un envoi interrompu, resté « en cours » bien au-delà de sa durée normale. */
  has_stuck: boolean;
  /** Une republication a un sens : soit un échec, soit un envoi interrompu. */
  can_retry: boolean;
};

/**
 * Agrège les distributions d'une publication en un seul état affichable.
 *
 * Raisonner sur la dernière distribution du tableau serait faux : avec plusieurs fiches,
 * une fiche en échec deviendrait invisible dès qu'une autre a réussi, et l'action de
 * republication ne serait jamais proposée.
 */
export const summarizeGmbDistributions = (
  distributions: ReadonlyArray<GmbDistributionLike> | null | undefined,
): GmbDistributionSummary => {
  const rows = distributions ?? [];
  const status = GMB_STATUS_PRIORITY.find((candidate): boolean =>
    rows.some((distribution): boolean => distribution.status === candidate),
  );

  const hasError = status === ProNewsDistributionStatus.ERROR;
  const hasStuck = rows.some((distribution): boolean => distribution.is_stuck === true);

  return {
    status,
    has_error: hasError,
    is_no_account: rows.some(
      (distribution): boolean =>
        distribution.status === ProNewsDistributionStatus.ERROR &&
        distribution.error_message === GMB_NO_ELIGIBLE_LOCATION_ERROR,
    ),
    has_stuck: hasStuck,
    // Un envoi interrompu n'est PAS un échec : personne n'a jamais écrit son résultat. Sans
    // cette porte de sortie, la fiche reste bloquée à vie et la republication n'est même pas
    // proposée au pro.
    can_retry: hasError || hasStuck,
  };
};

// ── Pro News Tag Schemas ─────────────────────────────────────────────

export const CreateProNewsTagRequestSchema = Type.Object({
  title: Type.String({
    placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_TAG_TITLE',
    title: 'INPUT_TITLE_PRO_NEWS_TAG_TITLE',
    minLength: 1,
    maxLength: 100,
  }),
  // Dérivé du titre à la création, puis figé : le slug est l'URL publique de la
  // catégorie sur le site du pro (?tag=<slug>). Modifiable explicitement.
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 100,
      meta: { input_type: 'slug', linked_field: 'title' },
    }),
  ),
  color: Type.Optional(
    Type.String({
      pattern: hexColorPattern,
      meta: { input_type: 'color_picker', options: COLOR_OPTIONS },
      title: 'INPUT_TITLE_PRO_NEWS_TAG_COLOR',
    }),
  ),
});
export type CreateProNewsTagRequestObject = Static<typeof CreateProNewsTagRequestSchema>;

export const UpdateProNewsTagRequestSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_TAG_TITLE',
      title: 'INPUT_TITLE_PRO_NEWS_TAG_TITLE',
      minLength: 1,
      maxLength: 100,
    }),
  ),
  // Renommer la catégorie ne touche PAS au slug (SlugInput arrive verrouillé en
  // édition) : les liens publics ?tag=<slug> déjà partagés restent valides.
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 100,
      meta: { input_type: 'slug', linked_field: 'title' },
    }),
  ),
  color: Type.Optional(
    Type.String({
      pattern: hexColorPattern,
      meta: { input_type: 'color_picker', options: COLOR_OPTIONS },
      title: 'INPUT_TITLE_PRO_NEWS_TAG_COLOR',
    }),
  ),
});
export type UpdateProNewsTagRequestObject = Static<typeof UpdateProNewsTagRequestSchema>;

export const ProNewsTagResponseSchema = Type.Object({
  pro_news_tag_id: Type.Number(),
  title: Type.String(),
  slug: Type.String(),
  color: Type.Optional(Type.String()),
  /** Nombre d'articles rattachés — renseigné par la liste back-office uniquement
   * (sert à prévenir le pro avant suppression). Absent côté site public. */
  news_count: Type.Optional(Type.Number()),
});
export type ProNewsTagResponseObject = Static<typeof ProNewsTagResponseSchema>;

// ── Pro News Schemas ─────────────────────────────────────────────────

/**
 * Fiches Google ciblées par la distribution GMB d'une publication.
 * Tableau VIDE = toutes les fiches publiables du pro : c'est le comportement historique
 * (et le seul qui inclut d'office une fiche connectée après la rédaction de l'article).
 * Le sélecteur ne propose que les fiches liées à un lieu, seules distribuables.
 */
export const GmbLocationSelectionSchema = Type.Array(Type.Integer({ minimum: 1 }), {
  minItems: 0,
  meta: { input_type: 'gmb_location_selector' },
  placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_GMB_LOCATIONS',
  title: 'INPUT_TITLE_PRO_NEWS_GMB_LOCATIONS',
});

/**
 * Question / réponse d'une publication. Saisie à part du corps (UX dédiée dans l'éditeur, accordéon
 * sous l'article sur le site, JSON-LD FAQPage) : la FAQ n'est plus une section du contenu.
 */
export const ProNewsFaqItemSchema = Type.Object({
  question: Type.String({
    minLength: 1,
    maxLength: 255,
    placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_FAQ_QUESTION',
    title: 'INPUT_TITLE_PRO_NEWS_FAQ_QUESTION',
  }),
  answer: Type.String({
    minLength: 1,
    maxLength: 2000,
    meta: { multiline: true },
    placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_FAQ_ANSWER',
    title: 'INPUT_TITLE_PRO_NEWS_FAQ_ANSWER',
  }),
});
export type ProNewsFaqItemObject = Static<typeof ProNewsFaqItemSchema>;

export const PRO_NEWS_FAQ_MAX_ITEMS = 10;

const ProNewsFaqSchema = Type.Array(ProNewsFaqItemSchema, {
  minItems: 0,
  maxItems: PRO_NEWS_FAQ_MAX_ITEMS,
  meta: { sortable: true },
  title: 'INPUT_TITLE_PRO_NEWS_FAQ',
  placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_FAQ',
});

export const CreateProNewsRequestSchema = Type.Object({
  title: Type.String({
    meta: { ai: { kind: AiFieldKind.ARTICLE_TITLE } },
    placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_TITLE',
    title: 'INPUT_TITLE_PRO_NEWS_TITLE',
    minLength: 3,
    maxLength: 255,
  }),
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 255,
      meta: { input_type: 'slug', linked_field: 'title' },
    }),
  ),
  content: Type.String({
    meta: {
      input_type: 'rich_text',
      allowed_tags: PRO_NEWS_CONTENT_TAGS,
      ai: { kind: AiFieldKind.ARTICLE_BODY },
    },
    placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_CONTENT',
    title: 'INPUT_TITLE_PRO_NEWS_CONTENT',
    maxLength: 50000,
  }),
  excerpt: Type.Optional(
    Type.String({
      meta: { ai: { kind: AiFieldKind.SUMMARY } },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_EXCERPT',
      title: 'INPUT_TITLE_PRO_NEWS_EXCERPT',
      maxLength: 500,
    }),
  ),
  faq: Type.Optional(ProNewsFaqSchema),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      minItems: 0,
      maxItems: 10,
      meta: { picture: true, multiple: true, crop_ratio: 3 / 2 },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_PICTURES',
      title: 'INPUT_TITLE_PRO_NEWS_PICTURES',
    }),
  ),
  /**
   * Optionnel : sans auteur, l'article est publié au nom de l'établissement (JSON-LD `author` =
   * l'entreprise). `none_option` : le sélecteur propose ce choix explicitement ; `all_members` :
   * signer un article ne demande pas d'agenda.
   */
  author_team_member_id: Type.Optional(
    Type.Integer({
      minimum: 1,
      meta: { input_type: 'team_member_selector', none_option: 'INPUT_OPTION_PRO_NEWS_AUTHOR_NONE', all_members: true },
      title: 'INPUT_TITLE_PRO_NEWS_AUTHOR',
    }),
  ),
  pro_news_tags: Type.Optional(
    Type.Array(Type.Integer({ minimum: 1 }), {
      minItems: 0,
      maxItems: maxNewsTagSelectable,
      meta: { input_type: 'pro_news_tag_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_TAGS',
      title: 'INPUT_TITLE_PRO_NEWS_TAGS',
    }),
  ),
  distribute_to_gmb: Type.Optional(
    Type.Boolean({
      title: 'INPUT_TITLE_PRO_NEWS_DISTRIBUTE_GMB',
      default: false,
    }),
  ),
  /**
   * Mot-clé cible de l'article (note SEO de l'éditeur : famille « Ciblage » évaluée seulement s'il
   * est renseigné). Écrit aussi par le moteur SEO pour les articles rédigés pour le pro.
   */
  focus_keyword: Type.Optional(
    Type.String({
      maxLength: 255,
      meta: { group: 'advanced' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_FOCUS_KEYWORD',
      title: 'INPUT_TITLE_PRO_NEWS_FOCUS_KEYWORD',
    }),
  ),
  google_location_ids: Type.Optional(GmbLocationSelectionSchema),
});
export type CreateProNewsRequestObject = Static<typeof CreateProNewsRequestSchema>;

export const UpdateProNewsRequestSchema = Type.Object({
  title: Type.Optional(
    Type.String({
      meta: { ai: { kind: AiFieldKind.ARTICLE_TITLE } },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_TITLE',
      title: 'INPUT_TITLE_PRO_NEWS_TITLE',
      minLength: 3,
      maxLength: 255,
    }),
  ),
  slug: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_PRO_SLUG',
      title: 'INPUT_TITLE_PRO_SLUG',
      maxLength: 255,
      meta: { input_type: 'slug', linked_field: 'title' },
    }),
  ),
  content: Type.Optional(
    Type.String({
      meta: {
        input_type: 'rich_text',
        allowed_tags: PRO_NEWS_CONTENT_TAGS,
        ai: { kind: AiFieldKind.ARTICLE_BODY },
      },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_CONTENT',
      title: 'INPUT_TITLE_PRO_NEWS_CONTENT',
      maxLength: 50000,
    }),
  ),
  excerpt: Type.Optional(
    Type.String({
      meta: { ai: { kind: AiFieldKind.SUMMARY } },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_EXCERPT',
      title: 'INPUT_TITLE_PRO_NEWS_EXCERPT',
      maxLength: 500,
    }),
  ),
  faq: Type.Optional(ProNewsFaqSchema),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      minItems: 0,
      maxItems: 10,
      meta: { picture: true, multiple: true, crop_ratio: 3 / 2 },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_PICTURES',
      title: 'INPUT_TITLE_PRO_NEWS_PICTURES',
    }),
  ),
  /**
   * `null` = retirer l'auteur (l'article repasse au nom de l'établissement) ; absent = inchangé.
   * `form_keep_null` : ce `null` traverse `normalizeFormValues` côté front ; `keep_null` : il traverse
   * le pipe de validation du backend. Sans eux, il serait écarté (absent = inchangé).
   */
  author_team_member_id: Type.Optional(
    Type.Union([Type.Integer({ minimum: 1 }), Type.Null()], {
      meta: {
        input_type: 'team_member_selector',
        none_option: 'INPUT_OPTION_PRO_NEWS_AUTHOR_NONE',
        all_members: true,
        form_keep_null: true,
        keep_null: true,
      },
      title: 'INPUT_TITLE_PRO_NEWS_AUTHOR',
    }),
  ),
  pro_news_tags: Type.Optional(
    Type.Array(Type.Integer({ minimum: 1 }), {
      minItems: 0,
      maxItems: maxNewsTagSelectable,
      meta: { input_type: 'pro_news_tag_selector' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_TAGS',
      title: 'INPUT_TITLE_PRO_NEWS_TAGS',
    }),
  ),
  distribute_to_gmb: Type.Optional(
    Type.Boolean({
      title: 'INPUT_TITLE_PRO_NEWS_DISTRIBUTE_GMB',
      default: false,
    }),
  ),
  /**
   * Mot-clé cible de l'article (note SEO de l'éditeur : famille « Ciblage » évaluée seulement s'il
   * est renseigné). Écrit aussi par le moteur SEO pour les articles rédigés pour le pro.
   */
  focus_keyword: Type.Optional(
    Type.String({
      maxLength: 255,
      meta: { group: 'advanced' },
      placeholder: 'INPUT_PLACEHOLDER_PRO_NEWS_FOCUS_KEYWORD',
      title: 'INPUT_TITLE_PRO_NEWS_FOCUS_KEYWORD',
    }),
  ),
  google_location_ids: Type.Optional(GmbLocationSelectionSchema),
});
export type UpdateProNewsRequestObject = Static<typeof UpdateProNewsRequestSchema>;

/**
 * Formulaires des comptes sans pack référencement : pas de note SEO, donc pas de mot-clé cible
 * (le champ n'aurait aucun effet visible). Définis ici : le front ne peut pas dériver un schéma
 * partagé avec son propre `Type.Omit` (deux instances de TypeBox, symboles `Kind` distincts).
 */
export const CreateProNewsWithoutSeoRequestSchema = Type.Omit(CreateProNewsRequestSchema, ['focus_keyword']);
export const UpdateProNewsWithoutSeoRequestSchema = Type.Omit(UpdateProNewsRequestSchema, ['focus_keyword']);

export const ScheduleProNewsRequestSchema = Type.Object({
  scheduled_publish_at: Type.String({
    format: 'date-time',
    title: 'INPUT_TITLE_PRO_NEWS_SCHEDULED_DATE',
    meta: { min_date_offset_days: 0 },
  }),
});
export type ScheduleProNewsRequestObject = Static<typeof ScheduleProNewsRequestSchema>;

export const ProNewsDistributionResponseSchema = Type.Object({
  pro_news_distribution_id: Type.Number(),
  channel: Type.String(),
  status: Type.String(),
  google_location_title: Type.Optional(Type.String()),
  external_post_id: Type.Optional(Type.String()),
  error_message: Type.Optional(Type.String()),
  is_stuck: Type.Optional(Type.Boolean()),
  sent_at_string: Type.Optional(Type.String({ format: 'date-time', meta: { is_short: true, with_time: true } })),
});
export type ProNewsDistributionResponseObject = Static<typeof ProNewsDistributionResponseSchema>;

/**
 * Origine moteur SEO d'une publication : mot-clé visé et verdict du contrôle qualité. Les conseils
 * de ce contrôle ne sont pas exposés : la note SEO de l'éditeur rejoue les mêmes règles en direct.
 */
export const ProNewsSeoOriginSchema = Type.Object({
  seo_article_id: Type.Integer(),
  keyword: Type.String(),
  qa_passed: Type.Boolean(),
  word_count: Type.Integer(),
});
export type ProNewsSeoOriginObject = Static<typeof ProNewsSeoOriginSchema>;

export const ProNewsResponseSchema = Type.Object({
  pro_news_id: Type.Number(),
  title: Type.String(),
  slug: Type.String(),
  content: Type.String(),
  excerpt: Type.Optional(Type.String()),
  pictures: Type.Optional(
    Type.Array(
      Type.Object(
        {
          small_url: Type.String({
            format: 'uri',
            meta: { [THUMBOR_META_KEY]: '100x100' },
          }),
          medium_url: Type.String({
            format: 'uri',
            meta: { [THUMBOR_META_KEY]: '200x200' },
          }),
          large_url: Type.String({
            format: 'uri',
            meta: { [THUMBOR_META_KEY]: '400x400' },
          }),
        },
        {
          meta: { picture: true },
        },
      ),
    ),
  ),
  status: Type.String(),
  published_at: Type.Optional(Type.String()),
  published_at_string: Type.Optional(Type.String({ format: 'date-time', meta: { is_short: true, with_time: false } })),
  scheduled_publish_at: Type.Optional(Type.String()),
  scheduled_publish_at_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { is_short: true, with_time: true } }),
  ),
  pro_news_tags: Type.Optional(Type.Array(ProNewsTagResponseSchema)),
  author: Type.Optional(
    Type.Object({
      team_member_id: Type.Number(),
      firstname: Type.Optional(Type.String()),
      lastname: Type.Optional(Type.String()),
      picture: Type.Optional(Type.Array(ResizedImageSchema)),
    }),
  ),
  author_team_member_id: Type.Optional(Type.Number()),
  distribute_to_gmb: Type.Optional(Type.Boolean()),
  // Vide = toutes les fiches publiables (cf. GmbLocationSelectionSchema).
  google_location_ids: Type.Optional(Type.Array(Type.Number())),
  distributions: Type.Optional(Type.Array(ProNewsDistributionResponseSchema)),
  /** Présent quand l'article a été rédigé pour le pro par le moteur SEO (plan MAX). */
  seo: Type.Optional(ProNewsSeoOriginSchema),
  // Entrées de la note SEO calculée dans l'éditeur et la liste (`scoreProNews`) : métas posées par le
  // moteur, mot-clé cible saisi par le pro ou par le moteur.
  meta_title: Type.Optional(Type.String()),
  meta_description: Type.Optional(Type.String()),
  focus_keyword: Type.Optional(Type.String()),
  /** Questions / réponses de la publication (éditées à part du corps). */
  faq: Type.Optional(Type.Array(ProNewsFaqItemSchema)),
  created_at: Type.String(),
  created_at_string: Type.Optional(Type.String({ format: 'date-time', meta: { is_short: true, with_time: false } })),
});
export type ProNewsResponseObject = Static<typeof ProNewsResponseSchema>;

export const ProNewsListResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(ProNewsResponseSchema),
});
export type ProNewsListResponseObject = Static<typeof ProNewsListResponseSchema>;

/** Tri de la liste des publications (`/marketing/publications`). */
export const PRO_NEWS_SORTS: SortConfig = {
  fields: {
    // Ordre historique de `ProNewsService.list`, défini ici seulement : `status` croissant suit
    // l'ordre de déclaration de l'enum Postgres `pro_news_status`.
    default: {
      label: 'SORT_FIELD_DEFAULT',
      value_type: 'fixed',
      default_direction: SortDirection.DESC,
      order_by: (): Record<string, unknown>[] => [
        { status: SortDirection.ASC },
        { published_at: sortNullsLast(SortDirection.DESC) },
        { scheduled_publish_at: sortNullsLast(SortDirection.ASC) },
        { created_at: SortDirection.DESC },
        { pro_news_id: SortDirection.DESC },
      ],
      api_description:
        "Ordre par défaut : articles actionnables d'abord — brouillons (plus récents en tête), planifiés (prochaine parution en tête), publiés (plus récents en tête), puis archivés. Sens unique (sort_dir ignoré).",
    },
    published_at: {
      label: 'SORT_FIELD_PUBLISHED_AT',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ published_at: sortNullsLast(direction) }],
      api_description:
        "Date de publication de l'article. Les articles jamais publiés (brouillons, planifiés) n'en ont pas et sont toujours placés en dernier, quel que soit le sens.",
    },
    created_at: {
      label: 'SORT_FIELD_CREATED_AT',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ created_at: direction }],
      api_description: "Date de création de l'article.",
    },
    title: {
      label: 'SORT_FIELD_TITLE',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ title: direction }],
      api_description: "Titre de l'article, par ordre alphabétique.",
    },
  },
  default_field: 'default',
  tiebreaker: 'pro_news_id',
};

// ── Public Pro News Schemas (website / public endpoints) ────────────

export const PublicProNewsItemSchema = Type.Object({
  pro_news_id: Type.Number(),
  title: Type.String(),
  slug: Type.String(),
  excerpt: Type.Optional(Type.String()),
  pictures: Type.Optional(Type.Array(ResizedImageSchema)),
  pro_news_tags: Type.Optional(Type.Array(ProNewsTagResponseSchema)),
  author_firstname: Type.Optional(Type.String()),
  author_lastname: Type.Optional(Type.String()),
  /** Sans auteur : l'article est publié au nom de l'établissement, dont voici le nom. */
  establishment_name: Type.Optional(Type.String()),
  published_at: Type.Optional(Type.String({ format: 'date-time' })),
  updated_at: Type.Optional(Type.String({ format: 'date-time' })),
  published_at_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { is_text_short: true, with_time: false, source_key: 'published_at' } }),
  ),
});
export type PublicProNewsItemObject = Static<typeof PublicProNewsItemSchema>;

export const PublicProNewsDetailSchema = Type.Object({
  pro_news_id: Type.Number(),
  title: Type.String(),
  slug: Type.String(),
  content: Type.String(),
  excerpt: Type.Optional(Type.String()),
  pictures: Type.Optional(Type.Array(ResizedImageSchema)),
  pro_news_tags: Type.Optional(Type.Array(ProNewsTagResponseSchema)),
  author_firstname: Type.Optional(Type.String()),
  author_lastname: Type.Optional(Type.String()),
  author_picture: Type.Optional(Type.Array(ResizedImageSchema)),
  /** Sans auteur : l'article est publié au nom de l'établissement (nom + logo en guise d'avatar). */
  establishment_name: Type.Optional(Type.String()),
  establishment_logo: Type.Optional(Type.Array(ResizedImageLogoSchema)),
  published_at: Type.Optional(Type.String({ format: 'date-time' })),
  updated_at: Type.Optional(Type.String({ format: 'date-time' })),
  published_at_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { is_text_short: true, with_time: false, source_key: 'published_at' } }),
  ),
  /** Questions / réponses de l'article, affichées en accordéon sous le corps et en JSON-LD FAQPage. */
  faq: Type.Optional(Type.Array(SeoArticleFaqItemSchema)),
});
export type PublicProNewsDetailObject = Static<typeof PublicProNewsDetailSchema>;

export const PublicProNewsListResponseSchema = Type.Object({
  news: Type.Array(PublicProNewsItemSchema),
  tags: Type.Array(ProNewsTagResponseSchema),
  total: Type.Number(),
});
export type PublicProNewsListResponseObject = Static<typeof PublicProNewsListResponseSchema>;
